import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Link, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import Login from './components/Login';
import DisciplineSelector from './components/DisciplineSelector';
import './App.css';

// API is proxied at /outcomes/api/* by nginx
// Use explicit full paths to ensure correct URL construction
axios.defaults.baseURL = '';
axios.defaults.withCredentials = true;

// Wrapper component for Dashboard with discipline param and admin check
function DashboardWithDiscipline({ user, readOnly }) {
  const { discipline = 'stem' } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAdminRequired();
  }, [discipline]);

  const checkAdminRequired = async () => {
    try {
      const response = await axios.get(`/outcomes/api/discipline-configs/${discipline}`);
      const isAdminRequired = response.data.admin_required;
      
      if (isAdminRequired) {
        // Check if user is authenticated and is an admin
        if (!user || !user.is_admin) {
          // Redirect to login with message
          navigate('/?accessDenied=' + encodeURIComponent(discipline));
          return;
        }
      }
    } catch (error) {
      console.error('Error checking discipline config:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return <Dashboard user={user} discipline={discipline} readOnly={readOnly} />;
}

// Wrapper component for AdminDashboard with discipline param
function AdminDashboardWithDiscipline({ user }) {
  const { discipline = 'stem' } = useParams();
  return <AdminDashboard user={user} discipline={discipline} />;
}

function AppRoutes({ user, handleLogout, loading }) {
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Routes>
      {/* User dashboard routes */}
      <Route path="/" element={<DisciplineSelector user={user} isAdmin={false} />} />
      <Route path="/:discipline" element={<DashboardWithDiscipline user={user} readOnly={!user?.is_admin} />} />
      
      {/* Admin routes - only accessible to admins */}
      <Route path="/admin" element={user?.is_admin ? <DisciplineSelector user={user} isAdmin={true} /> : <Login />} />
      <Route 
        path="/admin/:discipline" 
        element={
          user?.is_admin ? (
            <AdminDashboardWithDiscipline user={user} />
          ) : (
            <Login />
          )
        } 
      />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get('/outcomes/auth/user');
      setUser(response.data);
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/outcomes/auth/logout');
      setUser(null);
      window.location.href = '/outcomes';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <Router basename="/outcomes">
      <div className="App">
        <header className="app-header">
          <div className="header-content">
            <h1>Randolph School Outcomes Tracker</h1>
            <div className="header-actions">
              {user ? (
                <div className="user-section">
                  <span className="welcome-text">Welcome, {user.name}</span>
                  <button onClick={handleLogout} className="logout-btn">
                    Logout
                  </button>
                </div>
              ) : (
                <Link to="/admin" className="admin-link">
                  Admin Login
                </Link>
              )}
            </div>
          </div>
        </header>
        
        <AppRoutes user={user} handleLogout={handleLogout} loading={loading} />
      </div>
    </Router>
  );
}

export default App;
